import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiGet, apiPatch, type Trip, type TaxonomyRow } from '~/lib/api';

const MONTHS = ['—', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const STATUSES = ['draft', 'active', 'archived', 'paused'] as const;

export function TripEditor() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [species, setSpecies] = useState<TaxonomyRow[]>([]);
  const [rivers, setRivers] = useState<TaxonomyRow[]>([]);
  const [tripTypes, setTripTypes] = useState<TaxonomyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiGet<{ trip: Trip }>(`/api/admin/trips/${slug}`),
      apiGet<{ species: TaxonomyRow[]; rivers: TaxonomyRow[]; trip_types: TaxonomyRow[] }>(
        '/api/admin/taxonomy',
      ),
    ])
      .then(([tripRes, taxRes]) => {
        if (cancelled) return;
        setTrip(tripRes.trip);
        setSpecies(taxRes.species);
        setRivers(taxRes.rivers);
        setTripTypes(taxRes.trip_types);
      })
      .catch((e: Error) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  function patch<K extends keyof Trip>(key: K, value: Trip[K]) {
    setTrip((t) => (t ? { ...t, [key]: value } : t));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!trip) return;
    setSaving(true);
    setError(null);
    const { id, created_at, updated_at, species: _s, river: _r, trip_type: _tt, ...patchData } = trip;
    try {
      await apiPatch(`/api/admin/trips/${id}`, patchData);
      setSavedAt(new Date());
      if (patchData.slug !== slug) {
        navigate(`/trips/${patchData.slug}`, { replace: true });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="trc-page"><p>Loading…</p></div>;
  if (error && !trip) {
    return (
      <div className="trc-page">
        <Link to="/trips" className="trc-back">← Trips</Link>
        <p className="trc-error">Error: {error}</p>
      </div>
    );
  }
  if (!trip) return <div className="trc-page"><p>Not found.</p></div>;

  return (
    <div className="trc-page">
      <Link to="/trips" className="trc-back">← Trips</Link>
      <header className="trc-page-header">
        <p className="trc-eyebrow">Catalog · Trip</p>
        <h1 className="trc-osw trc-page-title">{trip.name}</h1>
      </header>

      <form onSubmit={handleSubmit} className="trc-form">

        {/* Basics */}
        <fieldset className="trc-fieldset">
          <legend>Basics</legend>
          <div className="trc-field">
            <label htmlFor="name">Trip name</label>
            <input id="name" value={trip.name} onChange={(e) => patch('name', e.target.value)} required />
          </div>
          <div className="trc-field">
            <label htmlFor="slug">URL slug</label>
            <input id="slug" value={trip.slug} onChange={(e) => patch('slug', e.target.value)} required />
            <small>Public URL: <code>/trips/{trip.slug}</code></small>
          </div>
          <div className="trc-field">
            <label htmlFor="status">Status</label>
            <select id="status" value={trip.status} onChange={(e) => patch('status', e.target.value as Trip['status'])}>
              {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
            <small>The public site only shows <code>active</code> trips.</small>
          </div>
        </fieldset>

        {/* Classification */}
        <fieldset className="trc-fieldset">
          <legend>Classification</legend>
          <div className="trc-field-row">
            <div className="trc-field">
              <label htmlFor="species">Species</label>
              <select id="species" value={trip.species_id ?? ''} onChange={(e) => patch('species_id', e.target.value || null)}>
                <option value="">—</option>
                {species.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
            </div>
            <div className="trc-field">
              <label htmlFor="river">River</label>
              <select id="river" value={trip.river_id ?? ''} onChange={(e) => patch('river_id', e.target.value || null)}>
                <option value="">—</option>
                {rivers.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
              </select>
            </div>
            <div className="trc-field">
              <label htmlFor="type">Trip type</label>
              <select id="type" value={trip.trip_type_id ?? ''} onChange={(e) => patch('trip_type_id', e.target.value || null)}>
                <option value="">—</option>
                {tripTypes.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
              </select>
            </div>
          </div>
        </fieldset>

        {/* Pricing & capacity */}
        <fieldset className="trc-fieldset">
          <legend>Pricing & Capacity</legend>
          <div className="trc-field-row">
            <div className="trc-field">
              <label htmlFor="price-adult">Adult price (USD)</label>
              <input
                id="price-adult"
                type="number"
                step="0.01"
                value={trip.price_adult_cents != null ? (trip.price_adult_cents / 100).toFixed(2) : ''}
                onChange={(e) => patch('price_adult_cents', e.target.value ? Math.round(parseFloat(e.target.value) * 100) : null)}
              />
            </div>
            <div className="trc-field">
              <label htmlFor="price-child">Child price (USD)</label>
              <input
                id="price-child"
                type="number"
                step="0.01"
                value={trip.price_child_cents != null ? (trip.price_child_cents / 100).toFixed(2) : ''}
                onChange={(e) => patch('price_child_cents', e.target.value ? Math.round(parseFloat(e.target.value) * 100) : null)}
              />
            </div>
            <div className="trc-field">
              <label htmlFor="capacity">Max anglers</label>
              <input id="capacity" type="number" min="1" value={trip.capacity}
                onChange={(e) => patch('capacity', parseInt(e.target.value, 10) || 1)} />
            </div>
          </div>
        </fieldset>

        {/* Schedule */}
        <fieldset className="trc-fieldset">
          <legend>Schedule</legend>
          <div className="trc-field-row">
            <div className="trc-field">
              <label htmlFor="duration">Duration (minutes)</label>
              <input id="duration" type="number" min="0" value={trip.duration_minutes ?? ''}
                onChange={(e) => patch('duration_minutes', e.target.value ? parseInt(e.target.value, 10) : null)} />
              <small>{trip.duration_minutes ? `${(trip.duration_minutes / 60).toFixed(1)} hours` : ''}</small>
            </div>
            <div className="trc-field">
              <label htmlFor="season-start">Season start month</label>
              <select id="season-start" value={trip.season_start_month ?? ''}
                onChange={(e) => patch('season_start_month', e.target.value ? parseInt(e.target.value, 10) : null)}>
                <option value="">—</option>
                {MONTHS.slice(1).map((m, i) => (<option key={i + 1} value={i + 1}>{m}</option>))}
              </select>
            </div>
            <div className="trc-field">
              <label htmlFor="season-end">Season end month</label>
              <select id="season-end" value={trip.season_end_month ?? ''}
                onChange={(e) => patch('season_end_month', e.target.value ? parseInt(e.target.value, 10) : null)}>
                <option value="">—</option>
                {MONTHS.slice(1).map((m, i) => (<option key={i + 1} value={i + 1}>{m}</option>))}
              </select>
            </div>
          </div>
        </fieldset>

        {/* Content */}
        <fieldset className="trc-fieldset">
          <legend>Content</legend>
          <div className="trc-field">
            <label htmlFor="meeting">Meeting point</label>
            <input id="meeting" value={trip.meeting_point ?? ''}
              onChange={(e) => patch('meeting_point', e.target.value || null)}
              placeholder="e.g. Bings Landing, Sterling, AK" />
          </div>
          <div className="trc-field">
            <label htmlFor="description">Description (Markdown)</label>
            <textarea id="description" rows={6} value={trip.description_md ?? ''}
              onChange={(e) => patch('description_md', e.target.value || null)} />
          </div>
          <div className="trc-field">
            <label htmlFor="included">What's included (Markdown)</label>
            <textarea id="included" rows={4} value={trip.included_md ?? ''}
              onChange={(e) => patch('included_md', e.target.value || null)} />
          </div>
        </fieldset>

        {/* Media */}
        <fieldset className="trc-fieldset">
          <legend>Media</legend>
          <div className="trc-field">
            <label htmlFor="hero">Hero image URL</label>
            <input id="hero" value={trip.hero_image_url ?? ''}
              onChange={(e) => patch('hero_image_url', e.target.value || null)} />
            {trip.hero_image_url && (
              <img src={trip.hero_image_url} alt="hero preview" className="trc-img-preview" />
            )}
          </div>
        </fieldset>

        {/* SEO */}
        <fieldset className="trc-fieldset">
          <legend>SEO</legend>
          <div className="trc-field">
            <label htmlFor="meta-title">Meta title</label>
            <input id="meta-title" value={trip.meta_title ?? ''}
              onChange={(e) => patch('meta_title', e.target.value || null)}
              placeholder={`${trip.name} | The River Crew`} />
          </div>
          <div className="trc-field">
            <label htmlFor="meta-desc">Meta description</label>
            <textarea id="meta-desc" rows={2} maxLength={160} value={trip.meta_description ?? ''}
              onChange={(e) => patch('meta_description', e.target.value || null)} />
          </div>
        </fieldset>

        {/* Integration */}
        <fieldset className="trc-fieldset">
          <legend>FareHarbor (integration metadata)</legend>
          <small className="trc-fieldset-help">
            When set, the booking flow dispatches to FareHarbor for availability + checkout.
            When empty, the trip is "owned" — bookings go directly to our DB.
          </small>
          <div className="trc-field-row">
            <div className="trc-field">
              <label htmlFor="fh-flow">FareHarbor flow ID</label>
              <input id="fh-flow" value={trip.fareharbor_flow_id ?? ''}
                onChange={(e) => patch('fareharbor_flow_id', e.target.value || null)} />
            </div>
            <div className="trc-field">
              <label htmlFor="fh-pk">FareHarbor item PK</label>
              <input id="fh-pk" value={trip.fareharbor_item_pk ?? ''}
                onChange={(e) => patch('fareharbor_item_pk', e.target.value || null)} />
            </div>
          </div>
        </fieldset>

        {error && <p className="trc-error">Error: {error}</p>}

        <div className="trc-form-actions">
          <button type="submit" disabled={saving} className="trc-button-primary">
            {saving ? 'Saving…' : 'Save'}
          </button>
          {savedAt && (
            <span className="trc-saved-marker">
              Saved {savedAt.toLocaleTimeString()}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
